import { useAtom } from 'jotai';
import { RefObject, useCallback, useRef } from 'react';
import { toaster } from '@optiaxiom/react';
import { addEdge, Connection, Edge, IsValidConnection, Node, NodeTypes } from '@xyflow/react';

import { workflowAgentAtom } from '@frontend/atoms';
import { useInstanceId } from '@frontend/hooks';
import { Step, Trigger } from '@frontend/types';
import { ForLoopStep } from '@frontend/types/workflow-agent';

import { generateNodeId } from '../services/workflow-id-utils';
import {
  isForLoopNode,
  isTriggerNodeType,
  StepNodeData,
  TriggerNodeData,
  WorkflowNodeData,
} from '../types';
import { HANDLE_IDS } from '../utils/constants';
import {
  evaluateAndReplaceNodeType,
  isSpecializedToConditionConnection,
} from '../utils/nodeDataMapping';

import { useConnectionValidation } from './useConnectionValidation';
import { useWorkflow } from './useWorkflow';
import { useWorkflowDnd } from './useWorkflowDnd';

export type UseWorkflowCanvasProps = {
  nodes: Node[];
  edges: Edge[];
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  onNodeDropped?: () => void;
  onAsyncNodeDropped?: (node: Node) => void;
  onFirstNodeDrop?: (updatedNodes: Node[]) => void;
};

export type UseWorkflowCanvasReturn = {
  // Event handlers
  onConnect: (params: Connection) => void;
  isValidConnection: IsValidConnection;
  onNodesDelete: (nodes: Node[]) => void;
  onEdgesDelete: (edges: Edge[]) => void;
  onNodeUpdate: (nodeId: string, data: Record<string, unknown>) => void;

  handleDragOver: (event: React.DragEvent) => void;
  handleDrop: (event: React.DragEvent) => void;

  nodeTypes: NodeTypes;
  reactFlowWrapper: RefObject<HTMLDivElement>;

  createAutoConnection: (triggerNode: Node, startStepNode: Node) => void;
  onDropNode: (
    nodeData: Step | Trigger,
    position: { x: number; y: number },
    forLoopContainer?: Node
  ) => void;
  onUpdateForLoopSteps: (forLoopNodeId: string, stepId: string, stepData: ForLoopStep) => void;
};

/**
 * Get the name from node data safely
 */
function getNodeName(data: WorkflowNodeData): string {
  return data.name || '';
}

/**
 * Get the description from node data safely
 */
function getNodeDescription(data: WorkflowNodeData): string | undefined {
  if ('description' in data) {
    return data.description ?? undefined;
  }
  return undefined;
}

function checkIsTriggerData(nodeData: Step | Trigger): nodeData is Trigger {
  return isTriggerNodeType(nodeData.type);
}

export function useWorkflowCanvas({
  nodes,
  edges,
  setNodes,
  setEdges,
  onNodeDropped,
  onAsyncNodeDropped,
  onFirstNodeDrop,
}: UseWorkflowCanvasProps): UseWorkflowCanvasReturn {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { validateConnection } = useConnectionValidation(nodes, edges);
  const { nodeTypes, getNodeConfig } = useWorkflow();
  const [workflowAgent] = useAtom(workflowAgentAtom);
  const { instanceId } = useInstanceId();

  /**
   * Find existing trigger nodes in the graph
   */
  const findExistingTriggers = useCallback((nodeList: Node[]): Node[] => {
    return nodeList.filter(node => isTriggerNodeType(node.type));
  }, []);

  /**
   * Find what trigger nodes are connected to
   */
  const findTriggerTarget = useCallback(
    (triggerNodes: Node[], edgeList: Edge[]): Node | null => {
      for (const trigger of triggerNodes) {
        const outgoingEdge = edgeList.find(edge => edge.source === trigger.id);
        if (outgoingEdge) {
          const targetNode = nodes.find(node => node.id === outgoingEdge.target);
          if (targetNode) {
            return targetNode;
          }
        }
      }
      return null;
    },
    [nodes]
  );

  /**
   * Auto-connection functionality for triggers
   */
  const createAutoConnection = useCallback(
    (triggerNode: Node, startStepNode: Node) => {
      const newEdge: Edge = {
        id: `${triggerNode.id}-${startStepNode.id}`,
        source: triggerNode.id,
        target: startStepNode.id,
        sourceHandle: HANDLE_IDS.SOURCE_RIGHT,
        targetHandle: HANDLE_IDS.TARGET_BODY,
        type: 'custom',
        deletable: true,
      };

      setEdges((eds: Edge[]) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  /**
   * Update ForLoop steps when a node is dropped inside.
   * Uses isForLoopNode type guard to properly narrow node.data to ForLoopStep.
   */
  const onUpdateForLoopSteps = useCallback(
    (forLoopNodeId: string, stepId: string, stepData: ForLoopStep) => {
      setNodes(nds =>
        nds.map(node => {
          if (node.id === forLoopNodeId && isForLoopNode(node)) {
            // node.data is now typed as ForLoopStep due to type guard
            const forLoopData = node.data;

            // Ensure steps object exists
            const currentSteps = forLoopData.steps || {};
            const updatedSteps = {
              ...currentSteps,
              [stepId]: stepData,
            };

            // Set as start step if it's the first step
            const forLoopDataWithStartStep = forLoopData as ForLoopStep & {
              start_step_id?: string;
            };
            const startStepId = forLoopDataWithStartStep.start_step_id || stepId;

            return {
              ...node,
              data: {
                ...forLoopData,
                steps: updatedSteps,
                start_step_id: startStepId,
              },
            };
          }

          // Also update parent ForLoop nodes if this is a nested ForLoop being updated
          if (isForLoopNode(node)) {
            const forLoopContainer = nodes.find(n => n.id === forLoopNodeId);
            if (forLoopContainer?.parentId === node.id) {
              // node.data is now typed as ForLoopStep due to type guard
              const parentForLoopData = node.data;
              const parentSteps = parentForLoopData.steps;

              if (parentSteps && parentSteps[forLoopNodeId]) {
                const updatedParentSteps = {
                  ...parentSteps,
                  [forLoopNodeId]: {
                    ...parentSteps[forLoopNodeId],
                  },
                };

                return {
                  ...node,
                  data: {
                    ...parentForLoopData,
                    steps: updatedParentSteps,
                  },
                };
              }
            }
          }

          return node;
        })
      );
    },
    [setNodes, nodes]
  );

  /**
   * Handle node drop from sidebar
   */
  const onDropNode = useCallback(
    (nodeData: Step | Trigger, position: { x: number; y: number }, forLoopContainer?: Node) => {
      let finalPosition = position;
      let parentId: string | undefined;
      let extent: 'parent' | undefined;

      if (forLoopContainer) {
        // Dropping inside ForLoop container
        parentId = forLoopContainer.id;
        extent = 'parent';

        // Adjust position to be relative to the ForLoop container
        const containerWidth = forLoopContainer.width || forLoopContainer.measured?.width || 320;
        const containerHeight = forLoopContainer.measured?.height || forLoopContainer.height || 188;

        finalPosition = {
          x: position.x - forLoopContainer.position.x,
          y: position.y - forLoopContainer.position.y,
        };

        // Ensure the node is positioned within the drop zone with better bounds
        const iconOffset = 48; // Space for the top-right icon
        const padding = 20;
        finalPosition.x = Math.max(
          padding,
          Math.min(finalPosition.x, containerWidth - padding - 240)
        );
        finalPosition.y = Math.max(
          padding + iconOffset,
          Math.min(finalPosition.y, containerHeight - padding - 60)
        );
      }

      // Generate node ID - use pure UUID for all nodes
      const newNodeId = generateNodeId();

      // Create typed node data - Step or Trigger (never Condition for dropped nodes)
      const typedNodeData = nodeData as TriggerNodeData | StepNodeData;

      // Use Node type for dynamic creation, then it becomes WorkflowNode in state
      const baseNode: Node = {
        id: newNodeId,
        type: nodeData.type,
        position: finalPosition,
        data: {
          ...typedNodeData,
          step_id: newNodeId,
        },
        deletable: true,
        parentId,
        extent,
      };

      // Add explicit dimensions for ForLoop nodes to support parent-child relationships
      const newNode: Node =
        nodeData.type === 'for_loop'
          ? {
              ...baseNode,
              style: {
                width: 320,
                height: 188,
              },
              width: 320,
              height: 188,
            }
          : baseNode;

      setNodes(nds => {
        const updatedNodes = nds.concat(newNode);

        // Auto-connect trigger based on new logic
        if (checkIsTriggerData(nodeData) && !forLoopContainer) {
          // Check existing triggers BEFORE adding the new one
          const existingTriggers = findExistingTriggers(nds);

          if (existingTriggers.length > 0) {
            // Subsequent trigger - connect to same target as existing triggers
            const existingTarget = findTriggerTarget(existingTriggers, edges);
            if (existingTarget) {
              createAutoConnection(newNode, existingTarget);
            }
          }
        }

        // Close any existing right sidebar when a new node is dropped
        if (onNodeDropped) {
          onNodeDropped();
        }

        // Check if this is an async node and trigger callback
        const nodeType = getNodeConfig(nodeData.type);
        if (nodeType?.onAsyncSave && onAsyncNodeDropped) {
          onAsyncNodeDropped(newNode);
        }

        // Check if this is the first node dropped and trigger auto-save
        if (onFirstNodeDrop && nds.length === 0) {
          onFirstNodeDrop(updatedNodes);
        }

        return updatedNodes;
      });

      // If dropping inside ForLoop, also update the ForLoop's steps data
      if (forLoopContainer) {
        // Create a step object for the ForLoop's internal steps tracking
        const isForLoop = nodeData.type === 'for_loop';
        const forLoopData = typedNodeData as ForLoopStep;

        const forLoopStep = {
          step_id: newNodeId,
          name: getNodeName(typedNodeData) || `Step ${newNodeId}`,
          description: getNodeDescription(typedNodeData) || '',
          type: nodeData.type,
          position: finalPosition,
          next_step_id: null,
          items_source: isForLoop ? forLoopData.items_source : '',
          item_name: isForLoop ? forLoopData.item_name : 'item',
          steps: isForLoop ? forLoopData.steps : {},
        } as ForLoopStep;

        onUpdateForLoopSteps(forLoopContainer.id, newNodeId, forLoopStep);
      }
    },
    [
      findExistingTriggers,
      findTriggerTarget,
      edges,
      setNodes,
      createAutoConnection,
      onUpdateForLoopSteps,
      onAsyncNodeDropped,
      onFirstNodeDrop,
      onNodeDropped,
      getNodeConfig,
    ]
  );

  // Drag and drop functionality
  const { onDragOver, onDrop } = useWorkflowDnd({
    reactFlowWrapper,
    nodes,
    onDropNode,
  });

  const isValidConnection: IsValidConnection = useCallback(
    (connection: Edge | Connection) => {
      const sourceNode = nodes.find(n => n.id === connection.source);
      const targetNode = nodes.find(n => n.id === connection.target);

      if (!sourceNode || !targetNode) return false;

      const validationResult = validateConnection(sourceNode, targetNode);

      return validationResult.isValid;
    },
    [nodes, validateConnection]
  );

  const onConnect = useCallback(
    (params: Connection) => {
      // First add the edge using addEdge
      setEdges((eds: Edge[]) =>
        addEdge(
          {
            ...params,
            deletable: true,
            type: 'custom',
          },
          eds
        )
      );

      // Find the source and target nodes for potential transformation
      const sourceNode = nodes.find(n => n.id === params.source);
      const targetNode = nodes.find(n => n.id === params.target);

      // Check if this connection should trigger a node replacement
      if (sourceNode && targetNode && isSpecializedToConditionConnection(sourceNode, targetNode)) {
        // Create the new edge for evaluation (since state update is async)
        const newEdge = {
          id: `${params.source}-${params.target}`,
          ...params,
          deletable: true,
          type: 'custom',
        } as Edge;

        const updatedEdges = [...edges, newEdge];

        // Evaluate if the source node needs replacement based on the new connection
        const replacementResult = evaluateAndReplaceNodeType(sourceNode.id, nodes, updatedEdges);

        if (
          replacementResult.shouldReplace &&
          replacementResult.newNode &&
          replacementResult.updatedEdges
        ) {
          // Replace the old node with the new one
          const updatedNodes = nodes
            .map(node => (node.id === sourceNode.id ? replacementResult.newNode : node))
            .filter((node): node is Node => node !== undefined);

          setNodes(updatedNodes);
          setEdges(replacementResult.updatedEdges);
        }
      }
    },
    [setEdges, nodes, setNodes, edges]
  );

  const onNodeUpdate = useCallback(
    (nodeId: string, data: Record<string, unknown>) => {
      const updatedNodes = nodes.map(node =>
        node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
      );

      setNodes(updatedNodes);
    },
    [nodes, setNodes]
  );

  /**
   * Helper function for sophisticated node deletion
   */
  const deleteSingleNode = useCallback(
    async (node: Node): Promise<boolean> => {
      const nodeType = getNodeConfig(node.type || '');

      // Check if node supports async deletion and has backend data
      if (nodeType?.onAsyncDelete && instanceId && workflowAgent.id) {
        const nodeData = node.data as Record<string, unknown>;
        const hasBackendData = nodeData && 'links' in nodeData && nodeData.links;

        if (hasBackendData) {
          try {
            const context = {
              instanceId,
              workflowAgentId: workflowAgent.id,
              nodeData,
            };

            await nodeType.onAsyncDelete(node.id, context);
            return true; // Indicates async deletion was used
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to delete node';
            toaster.create(errorMessage, { intent: 'danger' });
            throw error;
          }
        }
      }

      return false; // Indicates regular deletion should be used
    },
    [instanceId, workflowAgent.id, getNodeConfig]
  );

  const onNodesDelete = useCallback(
    async (deletedNodes: Node[]) => {
      const deletedNodeIds = deletedNodes.map(node => node.id);

      // Track which nodes were handled by async deletion
      const asyncDeletedNodes: string[] = [];
      let hasErrors = false;

      // Process each node for sophisticated deletion
      for (const node of deletedNodes) {
        try {
          const usedAsyncDelete = await deleteSingleNode(node);
          if (usedAsyncDelete) {
            asyncDeletedNodes.push(node.id);
          }
        } catch (_error) {
          hasErrors = true;
          break;
        }
      }

      if (hasErrors) {
        return;
      }

      // Show success toast for nodes that used regular deletion
      const regularDeletedNodes = deletedNodes.filter(node => !asyncDeletedNodes.includes(node.id));
      if (regularDeletedNodes.length > 0) {
        const message =
          regularDeletedNodes.length === 1
            ? 'Node deleted successfully'
            : `${regularDeletedNodes.length} nodes deleted successfully`;
        toaster.create(message, { intent: 'success' });
      }

      // Update local state - remove all deleted nodes
      const updatedNodes = nodes.filter(node => !deletedNodeIds.includes(node.id));

      // Remove edges connected to deleted nodes
      const updatedEdges = edges.filter(
        edge => !deletedNodeIds.includes(edge.source) && !deletedNodeIds.includes(edge.target)
      );

      // Check for reverse transformations after node deletion
      const affectedNodeIds = new Set<string>();
      deletedNodes.forEach(deletedNode => {
        if (deletedNode.type === 'condition') {
          edges.forEach(edge => {
            if (edge.target === deletedNode.id) {
              affectedNodeIds.add(edge.source);
            }
          });
        }
      });

      let currentNodes = updatedNodes;
      let currentEdges = updatedEdges;

      // Check each affected node for potential transformation
      affectedNodeIds.forEach(nodeId => {
        const replacementResult = evaluateAndReplaceNodeType(nodeId, currentNodes, currentEdges);

        if (
          replacementResult.shouldReplace &&
          replacementResult.newNode &&
          replacementResult.updatedEdges
        ) {
          currentNodes = currentNodes
            .map(node => (node.id === nodeId ? replacementResult.newNode : node))
            .filter((node): node is Node => node !== undefined);
          currentEdges = replacementResult.updatedEdges;
        }
      });

      setNodes(currentNodes);
      setEdges(currentEdges);
    },
    [nodes, edges, setNodes, setEdges, deleteSingleNode]
  );

  const onEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      const deletedEdgeIds = deletedEdges.map(edge => edge.id);
      const updatedEdges = edges.filter(edge => !deletedEdgeIds.includes(edge.id));
      setEdges(updatedEdges);

      // Check for reverse transformations after edge deletion
      const affectedNodeIds = new Set([
        ...deletedEdges.map(edge => edge.source),
        ...deletedEdges.map(edge => edge.target),
      ]);

      let currentNodes = nodes;
      let currentEdges = updatedEdges;

      affectedNodeIds.forEach(nodeId => {
        const replacementResult = evaluateAndReplaceNodeType(nodeId, currentNodes, currentEdges);

        if (
          replacementResult.shouldReplace &&
          replacementResult.newNode &&
          replacementResult.updatedEdges
        ) {
          currentNodes = currentNodes
            .map(node => (node.id === nodeId ? replacementResult.newNode : node))
            .filter((node): node is Node => node !== undefined);
          currentEdges = replacementResult.updatedEdges;
        }
      });

      if (currentNodes !== nodes) {
        setNodes(currentNodes);
      }
      if (currentEdges !== updatedEdges) {
        setEdges(currentEdges);
      }
    },
    [edges, setEdges, nodes, setNodes]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      if (onDragOver) {
        onDragOver(event);
      }
    },
    [onDragOver]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      if (onDrop) {
        onDrop(event);
      }
    },
    [onDrop]
  );

  return {
    onConnect,
    isValidConnection,
    onNodesDelete,
    onEdgesDelete,
    onNodeUpdate,
    handleDragOver,
    handleDrop,
    nodeTypes,
    reactFlowWrapper,
    createAutoConnection,
    onDropNode,
    onUpdateForLoopSteps,
  };
}
